// Time zones (TF-220): plain functions over IANA names ("Europe/Riga"), tested on their own
// (zones.test.ts). Every time in the app is shown in the viewer's zone; a time is entered as it is in a
// chosen zone; the browser's own zone list and words name them. Days in a zone came from My day (TF-61).

/** The zone the device is set to. */
export function deviceZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const partsFormat = new Map<string, Intl.DateTimeFormat>();

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function partsIn(zone: string, time: number): Parts {
  let format = partsFormat.get(zone);
  if (!format) {
    format = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    partsFormat.set(zone, format);
  }
  const parts: Record<string, string> = {};
  for (const part of format.formatToParts(time)) parts[part.type] = part.value;
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day), hour: Number(parts.hour), minute: Number(parts.minute), second: Number(parts.second) };
}

const ms = (time: Date | number | string) => (typeof time === 'number' ? time : typeof time === 'string' ? Date.parse(time) : time.getTime());
const pad = (n: number) => String(n).padStart(2, '0');

/** How far the zone's wall clock is ahead of UTC at this instant, in milliseconds. */
export function offsetAt(zone: string, time: number): number {
  const at = partsIn(zone, time);
  return Date.UTC(at.year, at.month - 1, at.day, at.hour, at.minute, at.second) - Math.floor(time / 1000) * 1000;
}

/** The instant a wall clock names in the zone: right on both sides of a clock change. */
function instantOfParts(zone: string, year: number, month: number, day: number, hour = 0, minute = 0): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const first = guess - offsetAt(zone, guess);
  return new Date(guess - offsetAt(zone, first));
}

/** The instant a day starts in the zone: `days` after the day `now` falls on there (0 = today). */
export function dayStart(zone: string, days = 0, now = Date.now()): Date {
  const today = partsIn(zone, now);
  const midnight = new Date(Date.UTC(today.year, today.month - 1, today.day + days));
  return instantOfParts(zone, midnight.getUTCFullYear(), midnight.getUTCMonth() + 1, midnight.getUTCDate());
}

/** The day an instant falls on in the zone, as YYYY-MM-DD. */
export function dayIn(zone: string, time: Date | number | string): string {
  const at = partsIn(zone, ms(time));
  return `${at.year}-${pad(at.month)}-${pad(at.day)}`;
}

/** Midnight exactly, in the zone. */
export function isMidnightIn(zone: string, time: Date | number | string): boolean {
  const at = partsIn(zone, ms(time));
  return at.hour === 0 && at.minute === 0;
}

/** An instant as the wall clock of the zone, "2026-10-05T14:30" - what the date and time fields show. */
export function wallIn(zone: string, time: Date | number | string): string {
  const at = partsIn(zone, ms(time));
  return `${at.year}-${pad(at.month)}-${pad(at.day)}T${pad(at.hour)}:${pad(at.minute)}`;
}

/** "14:30" in the zone. */
export const clockIn = (zone: string, time: Date | number | string) => wallIn(zone, time).slice(11);

/** The instant a wall clock ("2026-10-05T14:30") names in the zone; null for no or a broken value. */
export function instantOfWall(wall: string, zone: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number) as [number, number, number, number, number, number];
  return instantOfParts(zone, year, month, day, hour, minute);
}

/**
 * An instant as a browser-local Date that reads the zone's wall clock (getHours() is the hour there):
 * for code that works in local time - the calendar, its days and slots. Identity when the zone is the
 * device's.
 */
export function asLocalWall(time: Date | number | string, zone: string): Date {
  const at = partsIn(zone, ms(time));
  return new Date(at.year, at.month - 1, at.day, at.hour, at.minute, at.second);
}

/** The instant a browser-local Date's wall clock names in the zone: asLocalWall back. */
export function fromLocalWall(local: Date, zone: string): Date {
  return instantOfParts(zone, local.getFullYear(), local.getMonth() + 1, local.getDate(), local.getHours(), local.getMinutes());
}

/** Whether two zones show the same clock at an instant: no mark is needed between them then. */
export const sameClock = (a: string, b: string, time: Date | number | string) => a === b || offsetAt(a, ms(time)) === offsetAt(b, ms(time));

/** "Europe/Stockholm" -> "Stockholm", "America/Argentina/Buenos_Aires" -> "Buenos Aires". */
export const zoneCity = (zone: string) => (zone.split('/').pop() ?? zone).replace(/_/g, ' ');

/** "GMT+3", "GMT-4", "GMT+5:30" at an instant; "GMT" for UTC. */
export function zoneOffset(zone: string, time: Date | number | string = Date.now()): string {
  const minutes = Math.round(offsetAt(zone, ms(time)) / 60_000);
  if (minutes === 0) return 'GMT';
  const hours = Math.floor(Math.abs(minutes) / 60);
  const rest = Math.abs(minutes) % 60;
  return `GMT${minutes < 0 ? '-' : '+'}${hours}${rest ? `:${pad(rest)}` : ''}`;
}

const nameFormat = new Map<string, Intl.DateTimeFormat>();

/** The zone's name in a language, as the browser says it: "Central European Time", "Centrāleiropas laiks". */
export function zoneName(zone: string, language: string, time: Date | number | string = Date.now()): string {
  const key = `${language}|${zone}`;
  let format = nameFormat.get(key);
  if (!format) {
    try {
      format = new Intl.DateTimeFormat(language, { timeZone: zone, timeZoneName: 'longGeneric' });
    } catch {
      format = new Intl.DateTimeFormat(language, { timeZone: zone, timeZoneName: 'long' });
    }
    nameFormat.set(key, format);
  }
  return format.formatToParts(ms(time)).find((part) => part.type === 'timeZoneName')?.value ?? zone;
}

/** The zone in a few words: "Riga · GMT+3". */
export const zoneShort = (zone: string, time: Date | number | string = Date.now()) => `${zoneCity(zone)} · ${zoneOffset(zone, time)}`;

/** A few zones when the browser cannot list them (older browsers). */
const FALLBACK_ZONES = ['Europe/Riga', 'Europe/Tallinn', 'Europe/Vilnius', 'Europe/Helsinki', 'Europe/Stockholm', 'Europe/Warsaw', 'Europe/Berlin', 'Europe/London', 'Europe/Lisbon', 'America/New_York', 'UTC'];

let zones: string[] | null = null;

/** Every zone the browser knows, by name. */
export function allZones(): string[] {
  if (!zones) {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
    try {
      zones = intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : FALLBACK_ZONES;
    } catch {
      zones = FALLBACK_ZONES;
    }
    if (!zones.includes('UTC')) zones = [...zones, 'UTC'];
  }
  return zones;
}

/** Whether a name is a zone the browser can convert to. */
export function isZone(name: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name });
    return true;
  } catch {
    return false;
  }
}
